/**
 * Static Presentation Data Configuration
 *
 * This file contains static presentation metadata and configuration for offline mode.
 * When the backend is unavailable, the frontend can use this data to display presentations.
 *
 * SECURITY NOTE:
 * - This is intended for DEMO and INTERNAL USE ONLY
 * - Presentation content files are still loaded from the data/presentations directory
 * - This config only provides metadata for listing and access control in offline mode
 */

import type { PresentationData } from '../features/presentations/types'
import type { PresentationLibraryEntry } from '../features/presentations/services/PresentationService'

/**
 * Presentation metadata for offline/fallback mode
 * Matches the backend API response structure
 */
export interface StaticPresentationMetadata {
  id: string
  title: string
  description: string
  category: string
  language: string
  thumbnailUrl: string
  duration: string
  slideCount: number
  tags: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
  accessLevel: 'demo' | 'company' | 'admin'
  slug: string
}

/**
 * Static presentation library entries
 * These can be used when the backend API is unavailable
 */
export const STATIC_PRESENTATIONS: StaticPresentationMetadata[] = [
  {
    id: 'professional-layouts-demo',
    title: 'Professional Slide Layouts Demo',
    description: 'Showcasing modern, professional presentation layouts for business presentations',
    category: 'Design System',
    language: 'en',
    thumbnailUrl: '/images/presentations/professional-layouts-thumb.png',
    duration: '10 minutes',
    slideCount: 7,
    tags: ['Design System', 'Layouts', 'Professional', 'Demo'],
    isActive: true,
    accessLevel: 'demo',
    slug: 'professional-layouts-demo',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'developer-training-day1',
    title: 'AI Developer Training Day 1',
    description: 'AI Fundamentals & AI Coding Tools for Developers - Comprehensive introduction to AI development',
    category: 'Training',
    language: 'en',
    thumbnailUrl: '/images/presentations/developer-training-thumb.png',
    duration: '45 minutes',
    slideCount: 15,
    tags: ['AI', 'AI Tools', 'Development', 'Workshop'],
    isActive: true,
    accessLevel: 'demo',
    slug: 'developer-training-day1',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'entwicklerschulung-tag1',
    title: 'KI-Entwicklerschulung Tag 1',
    description: 'KI-Grundlagen & AI-Coding-Tools für Entwickler - Umfassende Einführung in die KI-Entwicklung',
    category: 'Training',
    language: 'de',
    thumbnailUrl: '/images/presentations/entwicklerschulung-thumb.png',
    duration: '60 minutes',
    slideCount: 39,
    tags: ['KI', 'AI-Tools', 'Entwicklung', 'Workshop'],
    isActive: true,
    accessLevel: 'demo',
    slug: 'entwicklerschulung-tag1',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'context-engineering-professional-en',
    title: 'Context Engineering - Professional Edition (English)',
    description: 'Advanced techniques for engineering and managing context in AI applications',
    category: 'Advanced AI',
    language: 'en',
    thumbnailUrl: '/images/presentations/context-engineering-en-thumb.png',
    duration: '90 minutes',
    slideCount: 45,
    tags: ['Context Engineering', 'AI', 'Advanced', 'Enterprise'],
    isActive: true,
    accessLevel: 'company',
    slug: 'context-engineering-professional-en',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'context-engineering-professional-de',
    title: 'Context Engineering - Professional Edition (German)',
    description: 'Fortgeschrittene Techniken für das Engineering und Management von Context in KI-Anwendungen',
    category: 'Advanced AI',
    language: 'de',
    thumbnailUrl: '/images/presentations/context-engineering-de-thumb.png',
    duration: '90 minutes',
    slideCount: 45,
    tags: ['Context Engineering', 'KI', 'Advanced', 'Enterprise'],
    isActive: true,
    accessLevel: 'company',
    slug: 'context-engineering-professional-de',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'erstgespraech',
    title: 'Erstgespräch: AI-Konsultierung',
    description: 'Erstgespräch und Bedarfsanalyse für AI-Projekte',
    category: 'Consulting',
    language: 'de',
    thumbnailUrl: '/images/presentations/erstgespraech-thumb.png',
    duration: '30 minutes',
    slideCount: 12,
    tags: ['Consulting', 'AI', 'Erstgespräch', 'Business'],
    isActive: true,
    accessLevel: 'admin',
    slug: 'erstgespraech',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
  {
    id: 'erstgespraech-en',
    title: 'Initial Consultation: AI Advisory',
    description: 'Initial consultation and requirements analysis for AI projects',
    category: 'Consulting',
    language: 'en',
    thumbnailUrl: '/images/presentations/erstgespraech-en-thumb.png',
    duration: '30 minutes',
    slideCount: 12,
    tags: ['Consulting', 'AI', 'Initial Meeting', 'Business'],
    isActive: true,
    accessLevel: 'admin',
    slug: 'erstgespraech-en',
    createdAt: '2025-10-01T00:00:00.000Z',
    updatedAt: '2025-10-01T00:00:00.000Z',
  },
]

/**
 * Get static presentation library entries
 * Converts static metadata to PresentationLibraryEntry format
 *
 * Note: This function only returns metadata. The actual presentation content
 * (slides) are loaded from the data/presentations directory by the PresentationService.
 *
 * @returns Array of presentation library entries compatible with offline mode
 */
export function getStaticPresentationLibrary(): PresentationLibraryEntry[] {
  // In offline mode, we return the static metadata
  // The actual slide data is loaded from TypeScript files in data/presentations/
  return STATIC_PRESENTATIONS.map((meta) => ({
    slug: meta.slug,
    data: {
      metadata: {
        title: meta.title,
        subtitle: meta.description,
        author: 'SecureSight.AI',
        date: meta.createdAt,
        description: meta.description,
        tags: meta.tags,
        duration: parseInt(meta.duration) || 30,
      },
      slides: [], // Slides are loaded from data/presentations/*.ts files
    } as PresentationData,
  }))
}

/**
 * Get static presentation metadata by slug
 *
 * @param slug - Presentation slug identifier
 * @returns Presentation metadata or undefined if not found
 */
export function getStaticPresentationBySlug(slug: string): StaticPresentationMetadata | undefined {
  return STATIC_PRESENTATIONS.find((p) => p.slug === slug)
}

/**
 * Get static presentations filtered by access level
 *
 * @param accessLevel - User access level (demo, company, admin)
 * @returns Array of presentations the user can access
 */
export function getStaticPresentationsByAccessLevel(
  accessLevel: 'demo' | 'company' | 'admin'
): StaticPresentationMetadata[] {
  const accessHierarchy = {
    demo: 0,
    company: 1,
    admin: 2,
  }

  const userLevel = accessHierarchy[accessLevel]

  return STATIC_PRESENTATIONS.filter((presentation) => {
    const presentationLevel = accessHierarchy[presentation.accessLevel]
    return userLevel >= presentationLevel
  })
}

/**
 * Check if offline mode is enabled for presentations
 *
 * @returns true if VITE_OFFLINE_MODE is set to 'true'
 */
export function isOfflineModeEnabled(): boolean {
  return import.meta.env.VITE_OFFLINE_MODE === 'true'
}

/**
 * Get all static presentations for offline use
 *
 * @returns Array of static presentation metadata
 */
export function getAllStaticPresentations(): StaticPresentationMetadata[] {
  return STATIC_PRESENTATIONS
}

/**
 * Get presentation count by category
 *
 * @returns Record mapping category names to presentation counts
 */
export function getPresentationCountByCategory(): Record<string, number> {
  return STATIC_PRESENTATIONS.reduce((acc, presentation) => {
    acc[presentation.category] = (acc[presentation.category] || 0) + 1
    return acc
  }, {} as Record<string, number>)
}

/**
 * Get all unique presentation tags
 *
 * @returns Array of unique tags across all presentations
 */
export function getAllPresentationTags(): string[] {
  const tags = new Set<string>()
  STATIC_PRESENTATIONS.forEach((presentation) => {
    presentation.tags.forEach((tag) => tags.add(tag))
  })
  return Array.from(tags).sort()
}

/**
 * Search presentations by query string
 *
 * @param query - Search query to match against title, description, and tags
 * @returns Array of matching presentations
 */
export function searchPresentations(query: string): StaticPresentationMetadata[] {
  const lowerQuery = query.toLowerCase()

  return STATIC_PRESENTATIONS.filter((presentation) => {
    return (
      presentation.title.toLowerCase().includes(lowerQuery) ||
      presentation.description.toLowerCase().includes(lowerQuery) ||
      presentation.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
      presentation.category.toLowerCase().includes(lowerQuery)
    )
  })
}

/**
 * Get presentations by language
 *
 * @param language - Language code (en, de, etc.)
 * @returns Array of presentations in the specified language
 */
export function getPresentationsByLanguage(language: string): StaticPresentationMetadata[] {
  return STATIC_PRESENTATIONS.filter((presentation) => presentation.language === language)
}

/**
 * Get available languages for presentations
 *
 * @returns Array of unique language codes
 */
export function getAvailableLanguages(): string[] {
  const languages = new Set<string>()
  STATIC_PRESENTATIONS.forEach((presentation) => {
    languages.add(presentation.language)
  })
  return Array.from(languages).sort()
}
